import React, { useState } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';

const ReportProblemModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    issueType: '',
    description: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      
      await supabaseClient.from('audit_logs').insert({
        event_category: 'support',
        event_type: 'problem_reported',
        actor_id: user?.id || null,
        actor_role: 'user',
        new_value: {
          issue_type: formData.issueType,
          description: formData.description,
          page: window.location.pathname,
          timestamp: new Date().toISOString()
        },
        risk_level: 'low',
        flagged: false,
        timestamp: new Date().toISOString()
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting report:', err);
      setError('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
        <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-md mx-4">
          <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
          <div className="text-center">
            <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Report Submitted</h3>
            <p className="text-neutral-600 mb-4">Your report has been submitted. We will follow up via email within 24 hours.</p>

            <div className="bg-neutral-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-neutral-600 mb-2">Need immediate help?</p>
              <a
                href="mailto:cani@controlpilotsystems.com"
                className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email us at cani@controlpilotsystems.com
              </a>
            </div>

            <a
              href="/faq"
              className="block text-primary-600 hover:text-primary-700 text-sm mb-4"
              onClick={onClose}
            >
              View FAQ for common questions
            </a>

            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-md mx-4">
        <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-neutral-900">Report a Problem</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl">✕</button>
        </div>

        <p className="text-sm text-neutral-600 mb-4">
          Have a question?{' '}
          <a
            href="/faq"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700 underline"
          >
            Check the FAQ first.
          </a>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="issue-type" className="block text-sm font-medium text-neutral-700 mb-1">Issue Type</label>
            <select
              id="issue-type"
              value={formData.issueType}
              onChange={(e) => setFormData({ ...formData, issueType: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2"
            >
              <option value="">Select issue type</option>
              <option value="payment_issue">Payment issue</option>
              <option value="member_issue">Member issue</option>
              <option value="technical_problem">Technical problem</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-neutral-700 mb-1">Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 h-32"
              placeholder="Please describe the issue you're experiencing..."
              required
            />
          </div>
          
          {error && (
            <p className="text-error-600 text-sm">{error}</p>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Submitting...' : 'Submit Report'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReportProblemModal;
